import { inject, injectable } from 'inversify'
import type { PaginationUrlFilterScalarValueType } from '@/services/tables/types'
import {
  EmailFilterOperation,
  type FilterToFormattedTextConvertable,
  type FormattedTextBadgesValueItem,
  type FormattedTextNode,
  type Operation,
} from '@/services/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type TranslateService from '@/services/translateService'
import TmLogicError from '@/core/error/tmLogicError'
import { coersceBoolean } from '@/utils/boolean/coersceBoolean'
import { getFullFilledOperations } from '@/services/filters/getFullFilledOperations'

interface ParsedValue {
  value: string[]
  hasValue: boolean
}

const divider = ', '

@injectable()
export default class EmailFilterMapper implements FilterToFormattedTextConvertable {
  constructor(@inject(SERVICE_TYPES.TranslateService) protected readonly translateService: TranslateService) {}

  public mapOperation(operation: Operation, value: unknown) {
    switch (operation) {
      case EmailFilterOperation.AnyOf:
        return EmailFilterOperation.AnyOf
      case EmailFilterOperation.NoneOf:
        return EmailFilterOperation.NoneOf
      case EmailFilterOperation.IsEmpty:
        return coersceBoolean(value, EmailFilterOperation.IsEmpty, EmailFilterOperation.IsNotEmpty)
      case EmailFilterOperation.IsUnsubscribed:
        return coersceBoolean(value, EmailFilterOperation.IsUnsubscribed, EmailFilterOperation.IsNotUnsubscribed)
      default:
        throw new TmLogicError('Unknown operation')
    }
  }

  public parseValue(value: unknown, emailFilterOperation: EmailFilterOperation): ParsedValue {
    if (
      emailFilterOperation === EmailFilterOperation.IsEmpty ||
      emailFilterOperation === EmailFilterOperation.IsNotEmpty ||
      emailFilterOperation === EmailFilterOperation.IsUnsubscribed ||
      emailFilterOperation === EmailFilterOperation.IsNotUnsubscribed
    ) {
      return {
        value: [],
        hasValue: true,
      }
    }
    return Array.isArray(value)
      ? {
          value,
          hasValue: value.length > 0,
        }
      : {
          value: [],
          hasValue: false,
        }
  }

  public getLabel(emailFilterOperation: EmailFilterOperation, parsedValue: ParsedValue) {
    const operationLabelMap: Record<EmailFilterOperation, string> = {
      [EmailFilterOperation.IsEmpty]: this.translateService.t('tmFilterEmail.options.isEmpty'),
      [EmailFilterOperation.IsNotEmpty]: this.translateService.t('tmFilterEmail.options.isNotEmpty'),
      [EmailFilterOperation.IsUnsubscribed]: this.translateService.t('tmFilterEmail.options.isUnsubscribed'),
      [EmailFilterOperation.IsNotUnsubscribed]: this.translateService.t('tmFilterEmail.options.isNotUnsubscribed'),
      [EmailFilterOperation.NoneOf]: this.translateService.t('tmFilterEmail.doesNotContain'),
      [EmailFilterOperation.AnyOf]: this.translateService.t('tmFilterEmail.contains'),
    }
    if ([EmailFilterOperation.AnyOf, EmailFilterOperation.NoneOf].includes(emailFilterOperation)) {
      return {
        op: operationLabelMap[emailFilterOperation],
        main: parsedValue.value.length > 0 ? parsedValue.value.join(divider) : this.translateService.t('common.any'),
      }
    }
    return {
      op: operationLabelMap[emailFilterOperation],
      main: '',
    }
  }

  public toFormattedText(filterValue: PaginationUrlFilterScalarValueType): FormattedTextNode[] {
    const operation = getFullFilledOperations(filterValue)[0]
    const value = filterValue[operation]
    const emailOperation = this.mapOperation(operation, value)
    const parsedValue = this.parseValue(value, emailOperation)
    const label = this.getLabel(emailOperation, parsedValue)

    const result: FormattedTextNode[] = []
    if (label.op) {
      result.push({ type: 'italic', value: label.op })
    }
    if (label.main) {
      result.push({ type: 'badges', value: this.getBadgesValueItemsFromLabel(label.main) })
    }
    return result
  }

  protected getBadgesValueItemsFromLabel(label: string): FormattedTextBadgesValueItem[] {
    return label.split(divider).map((part) => part)
  }
}
